import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

// Generic daily bars chart. Expects series aligned to daysInRange by index.
// Props:
// - title: string
// - daysInRange: string[] (YYYY-MM-DD)
// - series: Array<{ key: string, label: string, color: string, data: Array<{date: string, value: number}>, scale?: number }>
// - scalableKeys?: string[] (keys for which to render a slider)
// - labelSeriesKey?: string (series key to display label values for each day)
// - labelFormatter?: (raw: number) => string
// - rightControls?: React.ReactNode (custom controls rendered at right side of legend)
// - showValueLabels?: boolean (render raw value above each visible bar)
// - valueFormatter?: (raw: number) => string (format raw value labels)
// - perSeriesNormalize?: boolean (if true, each series uses its own max for height calc)
// - headroomRatio?: number (leave top space; e.g., 1.1 leaves ~10% headroom)
// - defaultVisibleKeys?: string[] (optional list of keys visible by default)
// - referenceLines?: Array<{ value: number, label: string, color: string, seriesKey: string, dash?: boolean }>
//   (horizontal reference lines, e.g. meta/media; positioned using seriesKey's scale/max)
// - holidays?: Record<string, string> (mapa 'YYYY-MM-DD' -> nombre del festivo; las barras
//   de esos días se pintan con `holidayColor` y su etiqueta de fecha se resalta)
// - holidayColor?: string (color para los días festivos; por defecto rosa/rojo)
const DailyBars = ({ title = 'Serie diaria', daysInRange = [], series = [], scalableKeys = [], labelSeriesKey, labelFormatter, rightControls, showValueLabels = false, valueFormatter, perSeriesNormalize = false, headroomRatio = 1.1, defaultVisibleKeys, referenceLines = [], footer = null, holidays = null, holidayColor = '#e11d48' }) => {
  // Normaliza el mapa de festivos a una función de consulta segura.
  const holidayNameOf = (date) => {
    if (!holidays) return null;
    if (holidays instanceof Map) return holidays.get(date) || null;
    return holidays[date] || null;
  };
  const hasAnyHoliday = useMemo(() => {
    if (!holidays) return false;
    return daysInRange.some((d) => !!holidayNameOf(d));
  }, [holidays, daysInRange]);
  const [visible, setVisible] = useState(() => Object.fromEntries(series.map(s => [s.key, Array.isArray(defaultVisibleKeys) ? defaultVisibleKeys.includes(s.key) : true])));
  const [scales, setScales] = useState(() => Object.fromEntries(series.map(s => [s.key, Number(s.scale || 1)])));
  const [showScales, setShowScales] = useState(false);
  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));
  const updateScale = (key, val) => setScales(m => ({ ...m, [key]: val }));

  // Sync state when series change (e.g., switching absolute vs per-kg)
  useEffect(() => {
    if (!Array.isArray(series)) return;
    setVisible(prev => {
      const next = {};
      for (const s of series) {
        const def = Array.isArray(defaultVisibleKeys) ? defaultVisibleKeys.includes(s.key) : true;
        next[s.key] = prev.hasOwnProperty(s.key) ? prev[s.key] : def;
      }
      return next;
    });
    setScales(prev => {
      const next = {};
      for (const s of series) {
        const def = Number(s.scale || 1);
        next[s.key] = prev.hasOwnProperty(s.key) ? prev[s.key] : def;
      }
      return next;
    });
  }, [series, defaultVisibleKeys]);

  // Estimate per-day width so multiple series don't squeeze to 0px
  const perDayMinWidth = useMemo(() => {
    const count = Array.isArray(series) ? series.length : 1;
    // ~14px per bar + 4px gaps -> conservative 18px per series, min 28px
    return Math.max(28, count * 18);
  }, [series]);

  const derivedMax = useMemo(() => {
    if (!daysInRange?.length) return 1;
    let maxVal = 0;
    for (let i = 0; i < daysInRange.length; i++) {
      for (const s of series) {
        if (!visible[s.key]) continue;
        const scale = Number(scales[s.key] ?? s.scale ?? 1);
        const v = Number(s.data?.[i]?.value || 0) * (isFinite(scale) && scale > 0 ? scale : 1);
        if (Number.isFinite(v) && v > maxVal) maxVal = v;
      }
    }
    return Number.isFinite(maxVal) ? Math.max(1, maxVal) : 1;
  }, [daysInRange, series, visible, scales]);

  // Compute per-series max values (post-scale) for normalization when enabled
  // Normalización por grupo: las series con el mismo `group` (misma unidad)
  // comparten escala, para que sus alturas sean comparables entre sí.
  const groupOf = (s) => s.group || s.key;
  const seriesMax = useMemo(() => {
    const m = {};
    for (const s of series) {
      if (!visible[s.key]) continue;
      const g = s.group || s.key;
      const scale = Number(scales[s.key] ?? s.scale ?? 1);
      let maxV = m[g] || 0;
      for (let i = 0; i < daysInRange.length; i++) {
        const v = Number(s.data?.[i]?.value || 0) * (isFinite(scale) && scale > 0 ? scale : 1);
        if (Number.isFinite(v) && v > maxV) maxV = v;
      }
      m[g] = maxV;
    }
    // Incluir líneas de referencia (meta/media) en el máximo de su grupo.
    for (const line of (Array.isArray(referenceLines) ? referenceLines : [])) {
      const s = series.find((x) => x.key === line.seriesKey);
      if (!s || !visible[line.seriesKey]) continue;
      const g = s.group || s.key;
      const scale = Number(scales[line.seriesKey] ?? s.scale ?? 1);
      const lv = Number(line.value || 0) * (isFinite(scale) && scale > 0 ? scale : 1);
      if (Number.isFinite(lv) && lv > (m[g] || 0)) m[g] = lv;
    }
    for (const k of Object.keys(m)) m[k] = Math.max(1, m[k]);
    return m;
  }, [series, daysInRange, visible, scales, referenceLines]);

  return (
    <Card className="mt-6 relative overflow-hidden bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900 dark:text-white">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Legend / Toggles */}
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
          {series.map(s => (
            <label
              key={s.key}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-all duration-200 ${
                visible[s.key]
                  ? 'bg-gray-50 dark:bg-gray-700/60 border-gray-200 dark:border-gray-600 shadow-sm'
                  : 'bg-transparent border-gray-100 dark:border-gray-700/40 opacity-50 hover:opacity-75'
              }`}
            >
              <input type="checkbox" className="accent-zinc-600 sr-only" checked={!!visible[s.key]} onChange={() => toggle(s.key)} />
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: s.color }} />
                <span className="font-medium text-gray-700 dark:text-gray-300">{s.label}</span>
                {((scales[s.key] ?? s.scale) && (scales[s.key] ?? s.scale) !== 1) ? (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-md bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 text-[10px] font-semibold">x{(scales[s.key] ?? s.scale)}</span>
                ) : null}
              </span>
            </label>
          ))}
          {hasAnyHoliday ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50/70 dark:bg-rose-900/20">
              <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: holidayColor }} />
              <span className="font-medium text-rose-700 dark:text-rose-300">★ Día festivo</span>
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            {rightControls ? rightControls : null}
            <button
              type="button"
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white transition-colors"
              onClick={() => setShowScales(v => !v)}
              title={showScales ? 'Ocultar escalas' : 'Mostrar escalas'}
            >
              {showScales ? 'Ocultar escalas' : 'Escalas'}
            </button>
          </div>
        </div>
        {showScales && scalableKeys?.length ? (
          <div className="mb-4 p-3 flex flex-wrap items-center gap-4 text-xs bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-100 dark:border-gray-700">
            {series.filter(s => scalableKeys.includes(s.key)).map(s => (
              <label key={`scale-${s.key}`} className="inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} /> <span className="text-gray-600 dark:text-gray-400">{s.label}</span></span>
                <input
                  type="range"
                  min={1}
                  max={200}
                  step={0.5}
                  className="w-24 accent-indigo-500"
                  value={Number(scales[s.key] ?? s.scale ?? 1)}
                  onChange={(e) => updateScale(s.key, Number(e.target.value))}
                />
                <span className="w-10 text-right font-semibold text-gray-700 dark:text-gray-300">x{Number(scales[s.key] ?? s.scale ?? 1)}</span>
              </label>
            ))}
          </div>
        ) : null}
        <div className="w-full overflow-x-auto rounded-xl">
          <div className="relative" style={{ minWidth: `${Math.max(700, daysInRange.length * perDayMinWidth)}px` }}>
            {Array.isArray(referenceLines) && referenceLines.length ? (
              <div className="pointer-events-none absolute left-0 right-0 top-0 z-10" style={{ height: '20rem' }}>
                {referenceLines.map((line, i) => {
                  const s = series.find((x) => x.key === line.seriesKey);
                  if (!s || !visible[line.seriesKey]) return null;
                  const scale = Number(scales[line.seriesKey] ?? s.scale ?? 1);
                  const denom = (perSeriesNormalize ? (seriesMax[groupOf(s)] || 1) : derivedMax) * (Number(headroomRatio) > 0 ? Number(headroomRatio) : 1);
                  const eff = Number(line.value) * (isFinite(scale) && scale > 0 ? scale : 1);
                  const bottomPct = (eff / denom) * 100;
                  if (!Number.isFinite(bottomPct) || !(Number(line.value) > 0) || bottomPct > 100) return null;
                  // Etiquetas al final (derecha). Se alternan arriba/abajo de la
                  // línea para que meta y media no se solapen cuando están cerca.
                  const above = i % 2 === 0;
                  const labelCls = above ? 'right-1 -top-0.5 -translate-y-full' : 'right-1 top-1';
                  return (
                    <div key={line.label} className="absolute left-0 right-0" style={{ bottom: `${bottomPct}%` }}>
                      <div className="w-full" style={{ borderTop: `3.5px ${line.dash ? 'dashed' : 'solid'} ${line.color}` }} />
                      <span
                        className={`absolute ${labelCls} text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/90 dark:bg-gray-800/90 shadow-sm ring-1 ring-black/5`}
                        style={{ color: line.color }}
                      >
                        {line.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          <div className="flex items-end gap-3" style={{ minWidth: `${Math.max(700, daysInRange.length * perDayMinWidth)}px` }}>
            {daysInRange.map((date, idx) => {
              const holidayName = holidayNameOf(date);
              const isHoliday = !!holidayName;
              return (
              <div key={date} className={`flex flex-col items-center justify-end h-80 group rounded-lg ${isHoliday ? 'bg-rose-50/70 dark:bg-rose-900/20' : ''}`}>
                <div className="relative flex items-end gap-1 h-full w-full justify-center">
                  {series.map(s => {
                    const scale = Number(scales[s.key] ?? s.scale ?? 1);
                    const raw = Number(s.data?.[idx]?.value || 0);
                    const val = raw * (isFinite(scale) && scale > 0 ? scale : 1);
                    if (!visible[s.key]) return null;
                    const denom = (perSeriesNormalize ? (seriesMax[groupOf(s)] || 1) : derivedMax) * (Number(headroomRatio) > 0 ? Number(headroomRatio) : 1);
                    const bottomPct = (val / denom) * 100;
                    const labelText = typeof valueFormatter === 'function' ? valueFormatter(raw) : `${raw.toFixed(2)}`;
                    const minBarPx = 3;
                    const barColor = isHoliday ? holidayColor : s.color;
                    return (
                      <div key={s.key} className="relative flex flex-col items-center justify-end w-3 md:w-3.5 h-full">
                        {(s.showLabel && raw > 0) ? (
                          <span
                            className="text-[10px] font-bold whitespace-nowrap leading-none mb-0.5 shrink-0"
                            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: barColor }}
                          >
                            {typeof s.labelFormat === 'function' ? s.labelFormat(raw) : Math.round(raw).toLocaleString()}
                          </span>
                        ) : null}
                        <div
                          className="w-full rounded-t-md shadow-sm hover:shadow-md transition-shadow duration-200"
                          style={{ height: `${bottomPct}%`, minHeight: `${minBarPx}px`, backgroundColor: barColor, opacity: 0.9 }}
                          title={`${date}${isHoliday ? ` — Festivo: ${holidayName}` : ''}: ${s.label} ${raw.toFixed(2)}${(scale && scale !== 1) ? ` (x${scale} → ${val.toFixed(2)})` : ''}`}
                        />
                        {showValueLabels && raw > 0 ? (
                          <span
                            className="absolute -translate-x-1/2 translate-y-[-4px] text-[10px] px-1.5 py-0.5 rounded-md bg-gray-900/80 dark:bg-gray-100/90 text-white dark:text-gray-900 font-medium whitespace-nowrap shadow-sm"
                            style={{ left: '50%', bottom: `${Math.min(96, Math.max(2, bottomPct))}%` }}
                            title={`Valor: ${raw.toFixed(2)}`}
                          >
                            {labelText}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                  {labelSeriesKey ? (() => {
                    const s = series.find(ss => ss.key === labelSeriesKey);
                    if (!s || !visible[labelSeriesKey]) return null;
                    const scale = Number(scales[labelSeriesKey] ?? s.scale ?? 1);
                    const raw = Number(s.data?.[idx]?.value || 0);
                    const val = raw * (isFinite(scale) && scale > 0 ? scale : 1);
                    const pct = Number.isFinite(raw) ? raw : 0;
                    const denom = (perSeriesNormalize ? (seriesMax[groupOf(s)] || 1) : derivedMax) * (Number(headroomRatio) > 0 ? Number(headroomRatio) : 1);
                    const bottomPct = (val / denom) * 100;
                    const text = typeof labelFormatter === 'function' ? labelFormatter(pct) : `${pct.toFixed(1)}`;
                    return (
                      <span
                        className="absolute -translate-x-1/2 translate-y-[-4px] text-[10px] px-1.5 py-0.5 rounded-md bg-gray-900/80 dark:bg-gray-100/90 text-white dark:text-gray-900 font-medium shadow-sm"
                        style={{ left: '50%', bottom: `${Math.min(98, Math.max(2, bottomPct))}%` }}
                        title={`Valor original: ${pct.toFixed(2)}`}
                      >
                        {text}
                      </span>
                    );
                  })() : null}
                </div>
                <div
                  className={`mt-1.5 text-[10px] font-medium transition-colors ${isHoliday ? 'font-semibold' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}
                  style={isHoliday ? { color: holidayColor } : undefined}
                  title={isHoliday ? `Festivo: ${holidayName}` : undefined}
                >
                  {isHoliday ? '★ ' : ''}{date.slice(5)}
                </div>
              </div>
              );
            })}
          </div>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">Altura relativa al máximo de las series visibles</div>
        {footer ? <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">{footer}</div> : null}
      </CardContent>
    </Card>
  );
};

export default DailyBars;
